/**
 * ExecutionHistorySidebar Component
 *
 * Sidebar que exibe o histórico de execuções de um workflow.
 * Permite selecionar uma execução para visualizar o status de cada node no canvas.
 *
 * Painel lateral do editor (w-[400px]) alinhado ao Design System (tokens var(--theme-*)).
 */

import React, { useMemo } from 'react';
import { useWorkflowExecutions, useWorkflowExecution } from '../hooks/useWorkflowExecution';
import type { WorkflowExecutionResponse } from '@troia-v3/types';
import { getTriggerTypeLabel } from '../utils/node-labels';
import { describeCancellation } from '../utils/cancellation-events';
import { describeSkippedSummary, summarizeSkippedNodes } from '../utils/skip-reasons';
import { isLongErrorText, listFailedNodes } from '../utils/failed-nodes';
import { StatusPill, Tooltip } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// Cores, ícones, rótulos e formatadores moraram aqui até 21/09/2026. Saíram
// para `utils/execution-display.ts` sem mudar um valor: a modal "Visualizar
// contexto" (spec 2026-09-20 §5.6) desenha a MESMA lista de execuções e as duas
// telas ficam a um clique uma da outra.
import {
  EXECUTION_STATUS_COLORS,
  EXECUTION_STATUS_ICONS,
  EXECUTION_STATUS_LABELS,
  formatDuration,
  formatFullDateTime,
  formatRelativeTime,
} from '../utils/execution-display';

export interface ExecutionHistorySidebarProps {
  /** ID do workflow para buscar execuções */
  workflowId: string;
  /**
   * Nós do desenho ATUAL (id + rótulo) — dão nome aos nós que falharam na
   * lista de cada execução. Id que não está no desenho aparece como "Nó não
   * identificado" (pode ter sido apagado, ou ser id interno do motor numa
   * execução gravada antes da Fase 1 — ver `UNKNOWN_NODE_LABEL`).
   */
  nodes: ReadonlyArray<{ id: string; data?: { label?: string } }>;
  /** Callback quando uma execução é selecionada */
  onSelectExecution: (execution: WorkflowExecutionResponse | null) => void;
  /** ID da execução atualmente selecionada */
  selectedExecutionId?: string;
  /** Callback para fechar a sidebar */
  onClose: () => void;
}

/**
 * Extrai o `resumeAt` de uma execução suspensa por horário comercial.
 * Varre o runData atrás da entry suspensa mais recente com
 * `suspendedPayload.reason === 'business_hours'`. Retorna null quando a
 * suspensão tem outro motivo (delay, aguardar evento) ou o payload é
 * irreconhecível — nesses casos fica só o label genérico "Aguardando retomada".
 */
function getBusinessHoursResumeAt(execution: WorkflowExecutionResponse): string | null {
  if (execution.status !== 'suspended' || !execution.runData) return null;
  for (const entries of Object.values(execution.runData)) {
    for (let i = entries.length - 1; i >= 0; i--) {
      const entry = entries[i];
      if (entry?.status !== 'suspended') continue;
      const payload = entry.suspendedPayload as
        | { reason?: unknown; resumeAt?: unknown }
        | null
        | undefined;
      if (
        payload !== null &&
        typeof payload === 'object' &&
        payload.reason === 'business_hours' &&
        typeof payload.resumeAt === 'string'
      ) {
        return payload.resumeAt;
      }
    }
  }
  return null;
}

/**
 * Calcula a duração de uma execução
 */
function calculateDuration(execution: WorkflowExecutionResponse): number | null {
  if (!execution.startedAt) return null;
  // Use completedAt for both success and failure (failedAt doesn't exist in the type)
  const endTime = execution.completedAt || (execution.status === 'running' ? new Date().toISOString() : execution.startedAt);
  const startTime = new Date(execution.startedAt).getTime();
  const end = new Date(endTime).getTime();
  return end - startTime;
}

/**
 * ExecutionHistorySidebar - Sidebar para exibir histórico de execuções
 */
export const ExecutionHistorySidebar: React.FC<ExecutionHistorySidebarProps> = ({
  workflowId,
  nodes,
  onSelectExecution,
  selectedExecutionId,
  onClose,
}) => {
  // Execuções com o bloco de erro expandido ("ver mais"). Estado local: some ao fechar a sidebar.
  const [expandedErrors, setExpandedErrors] = React.useState<ReadonlySet<string>>(() => new Set<string>());
  const toggleErrorExpanded = (executionId: string) => {
    setExpandedErrors((current) => {
      const next = new Set(current);
      if (next.has(executionId)) next.delete(executionId);
      else next.add(executionId);
      return next;
    });
  };
  // Buscar lista de execuções
  const {
    data: executionsData,
    isLoading,
    error,
  } = useWorkflowExecutions(workflowId, { limit: 50 });

  // Buscar detalhes da execução selecionada
  const { data: selectedExecution } = useWorkflowExecution(selectedExecutionId);

  // Notificar quando a execução selecionada mudar
  React.useEffect(() => {
    if (selectedExecution) {
      onSelectExecution(selectedExecution);
    }
  }, [selectedExecution, onSelectExecution]);

  // Execuções ordenadas por data (mais recente primeiro)
  const executions = useMemo(() => {
    if (!executionsData?.items) return [];
    return [...executionsData.items].sort((a, b) => {
      // startedAt is the only reliable timestamp (createdAt doesn't exist in the type)
      const dateA = new Date(a.startedAt || '').getTime();
      const dateB = new Date(b.startedAt || '').getTime();
      return dateB - dateA;
    });
  }, [executionsData]);

  // Handler para selecionar uma execução
  const handleSelectExecution = (execution: WorkflowExecutionResponse) => {
    if (selectedExecutionId === execution.id) {
      // Desselecionar se já está selecionada
      onSelectExecution(null);
    } else {
      onSelectExecution(execution);
    }
  };

  // Handler para limpar seleção
  const handleClearSelection = () => {
    onSelectExecution(null);
  };

  return (
    <div
      data-testid="execution-history-sidebar"
      className="flex flex-col md:w-[400px] md:max-w-full md:border-l md:h-full max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-[70] max-md:max-h-[75vh] max-md:rounded-t-2xl max-md:border-t max-md:shadow-[0_-8px_30px_rgba(0,0,0,0.18)] max-md:[background:var(--theme-overlay-card-background)] md:[background-color:var(--theme-card-background)]"
      style={{
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b flex items-center gap-2.5 flex-shrink-0"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <Icon name="clock" className="w-[18px] h-[18px] flex-none" style={{ color: 'var(--theme-muted-text)' }} />
        <h2 className="font-bold text-sm flex-1 min-w-0 truncate" style={{ color: 'var(--theme-text)' }}>
          Histórico de execuções
        </h2>
        <button
          type="button"
          onClick={onClose}
          data-testid="execution-history-close"
          aria-label="Fechar histórico"
          className="w-[30px] h-[30px] rounded-lg inline-flex items-center justify-center transition-colors flex-none"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            e.currentTarget.style.color = 'var(--theme-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          <Icon name="x" className="w-[18px] h-[18px]" />
        </button>
      </div>

      {/* Selected Execution Banner */}
      {selectedExecutionId && (
        <div
          className="px-4 py-2 border-b flex items-center justify-between flex-shrink-0"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--theme-primary) 10%, transparent)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <span className="text-xs font-medium" style={{ color: 'var(--theme-primary)' }}>
            Visualizando execução selecionada
          </span>
          <button
            type="button"
            onClick={handleClearSelection}
            className="text-xs font-medium px-2 py-0.5 rounded transition-colors"
            style={{ color: 'var(--theme-primary)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--theme-primary) 16%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Limpar
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="loader" className="w-8 h-8 animate-spin"
              style={{ color: 'var(--theme-muted-text)' }} />
            <p className="mt-2 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              Carregando execuções...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Icon name="alert-circle" className="w-8 h-8" style={{ color: 'var(--theme-error-color)' }} />
            <p className="mt-2 text-sm text-center" style={{ color: 'var(--theme-error-color)' }}>
              Erro ao carregar execuções
            </p>
            <p className="mt-1 text-xs text-center" style={{ color: 'var(--theme-muted-text)' }}>
              {error.message}
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && executions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Icon name="clock" className="w-8 h-8" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
            <p className="mt-2 text-sm text-center" style={{ color: 'var(--theme-muted-text)' }}>
              Nenhuma execução encontrada
            </p>
            <p className="mt-1 text-xs text-center" style={{ color: 'var(--theme-muted-text)', opacity: 0.8 }}>
              Execute o workflow para ver o histórico aqui
            </p>
          </div>
        )}

        {/* Executions List */}
        {!isLoading && !error && executions.length > 0 && (
          <div className="flex flex-col gap-2.5">
            {executions.map((execution) => {
              const statusIcon = (EXECUTION_STATUS_ICONS[execution.status] || 'clock') as IconName;
              const statusColor = EXECUTION_STATUS_COLORS[execution.status] || 'var(--theme-muted-text)';
              const statusLabel = EXECUTION_STATUS_LABELS[execution.status] || execution.status;
              const duration = calculateDuration(execution);
              const nodeCount = execution.nodeOrder?.length ?? 0;
              // Meta em uma linha (mockup .exe-meta): gatilho · N nodes · duração
              const metaParts = [
                execution.triggerType ? getTriggerTypeLabel(execution.triggerType) : null,
                nodeCount > 0 ? `${nodeCount} nodes` : null,
                duration !== null ? formatDuration(duration) : null,
              ].filter((part): part is string => part !== null);
              const isSelected = selectedExecutionId === execution.id;
              const isRunning = execution.status === 'running';
              // U3 / D20: texto do erro + nós que falharam (última entrada `failed` em runData).
              const errorText = execution.status === 'failed' && execution.error ? execution.error : null;
              const failedNodes = listFailedNodes(execution.runData, nodes);
              const isErrorExpanded = expandedErrors.has(execution.id);
              const canToggleError =
                (errorText !== null && isLongErrorText(errorText)) ||
                failedNodes.some((line) => isLongErrorText(`${line.label} — ${line.error}`, 2));

              return (
                <div
                  key={execution.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  data-testid={`execution-card-${execution.id}`}
                  onClick={() => handleSelectExecution(execution)}
                  onKeyDown={(e) => {
                    // Só reage com o foco no PRÓPRIO card: Enter/Espaço no "ver mais"
                    // (botão filho) não pode selecionar a execução.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectExecution(execution);
                    }
                  }}
                  className="w-full rounded-[10px] border p-3 text-left transition-colors cursor-pointer"
                  style={{
                    borderColor: isSelected ? 'var(--theme-primary)' : 'var(--theme-card-border)',
                    backgroundColor: isSelected
                      ? 'color-mix(in srgb, var(--theme-primary) 10%, transparent)'
                      : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = 'var(--theme-input-border)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = 'var(--theme-card-border)';
                    }
                  }}
                >
                  {/* Status pill + Duration */}
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[11px] font-bold flex-none"
                      style={{
                        color: statusColor,
                        backgroundColor: `color-mix(in srgb, ${statusColor} 13%, transparent)`,
                      }}
                    >
                      <Icon
                        name={statusIcon}
                        className={`w-3 h-3 ${isRunning ? 'animate-spin' : ''}`}
                      />
                      {statusLabel}
                    </span>
                    {execution.startedAt && (
                      <span
                        className="ml-auto text-[11px] tabular-nums flex-none"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {formatRelativeTime(execution.startedAt)}
                      </span>
                    )}
                  </div>

                  {/* Meta: gatilho · nodes · duração (mockup .exe-meta) */}
                  {metaParts.length > 0 && (
                    <p
                      className="text-[11.5px] mt-1.5"
                      style={{ color: 'var(--theme-muted-text)' }}
                    >
                      {metaParts.join(' · ')}
                    </p>
                  )}

                  {/* Selos da falha (U3 / D20). StatusPill não repassa ref — o <span> é o gatilho do Tooltip. */}
                  {(execution.partialFailure === true ||
                    execution.customerStateFailure === true ||
                    execution.interrupted === true) && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {execution.partialFailure === true && (
                        <Tooltip
                          content="Parte dos caminhos paralelos falhou e parte entregou. Conta para a pausa automática."
                          className="max-w-[260px] whitespace-normal"
                        >
                          <span className="inline-flex">
                            <StatusPill
                              variant="warning"
                              label="Falha parcial"
                              data-testid={`execution-flag-partial-${execution.id}`}
                            />
                          </span>
                        </Tooltip>
                      )}
                      {execution.customerStateFailure === true && (
                        <Tooltip
                          content="A falha veio do estado do cliente (janela de 24h fechada, número sem WhatsApp…). Não conta para a pausa automática."
                          className="max-w-[260px] whitespace-normal"
                        >
                          <span className="inline-flex">
                            <StatusPill
                              variant="info"
                              label="Falha do cliente"
                              data-testid={`execution-flag-customer-${execution.id}`}
                            />
                          </span>
                        </Tooltip>
                      )}
                      {execution.interrupted === true && (
                        <Tooltip
                          content="O servidor parou no meio desta execução (deploy ou queda). Não foi o workflow que falhou."
                          className="max-w-[260px] whitespace-normal"
                        >
                          <span className="inline-flex">
                            <StatusPill
                              variant="neutral"
                              label="Interrompida"
                              data-testid={`execution-flag-interrupted-${execution.id}`}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {/* Suspensa por horário comercial — várias linhas; a data completa vai no Tooltip */}
                  {execution.status === 'suspended' && (() => {
                    const resumeAt = getBusinessHoursResumeAt(execution);
                    if (!resumeAt) return null;
                    return (
                      <Tooltip content={`Retoma em ${formatFullDateTime(resumeAt)}`}>
                        <p
                          data-testid="execution-suspended-reason"
                          className="text-xs mt-2 whitespace-pre-wrap break-words rounded-md px-2 py-1.5"
                          style={{
                            color: 'var(--theme-warning-color)',
                            backgroundColor: 'color-mix(in srgb, var(--theme-warning-color) 8%, transparent)',
                          }}
                        >
                          Aguardando horário comercial — retoma {formatRelativeTime(resumeAt)}
                        </p>
                      </Tooltip>
                    );
                  })()}

                  {/* Motivo do cancelamento (spec 2026-09-14, Fase 2) */}
                  {execution.status === 'cancelled' && execution.cancelledBy !== undefined && (
                    <p
                      data-testid="execution-cancelled-reason"
                      className="mt-1 text-xs"
                      style={{ color: 'var(--theme-muted-text)' }}
                    >
                      {describeCancellation(execution.cancelledBy)}
                    </p>
                  )}

                  {/* Resumo de nós pulados (aba Filtros / cancelamento) — Fase 4 */}
                  {(() => {
                    const resumo = describeSkippedSummary(summarizeSkippedNodes(execution.runData));
                    if (resumo === null) return null;
                    return (
                      <p
                        data-testid="execution-skipped-summary"
                        className="mt-1 text-xs"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {resumo}
                      </p>
                    );
                  })()}

                  {/* Falha (U3 / D20): erro em várias linhas + nós que falharam; recolhido com "ver mais" */}
                  {(errorText !== null || failedNodes.length > 0) && (
                    <div
                      className="mt-2 rounded-md px-2 py-1.5"
                      style={{ backgroundColor: 'color-mix(in srgb, var(--theme-error-color) 8%, transparent)' }}
                    >
                      {errorText !== null && (
                        <p
                          data-testid={`execution-error-${execution.id}`}
                          className={`text-xs whitespace-pre-wrap break-words ${isErrorExpanded ? '' : 'line-clamp-4'}`}
                          style={{ color: 'var(--theme-error-color)' }}
                        >
                          {errorText}
                        </p>
                      )}

                      {failedNodes.length > 0 && (
                        <div
                          className={errorText !== null ? 'mt-1.5 pt-1.5 border-t' : ''}
                          style={errorText !== null ? { borderColor: 'var(--theme-card-border)' } : {}}
                        >
                          <p className="text-[11px] font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
                            {failedNodes.length === 1 ? 'Nó que falhou' : 'Nós que falharam'}
                          </p>
                          <ul
                            data-testid={`execution-failed-nodes-${execution.id}`}
                            className="mt-1 flex flex-col gap-1"
                          >
                            {failedNodes.map((line) => (
                              <li
                                key={line.nodeId}
                                data-testid={`execution-failed-node-${execution.id}-${line.nodeId}`}
                                className={`text-xs whitespace-pre-wrap break-words ${isErrorExpanded ? '' : 'line-clamp-2'}`}
                                style={{ color: 'var(--theme-muted-text)' }}
                              >
                                <span className="font-semibold" style={{ color: 'var(--theme-text)' }}>
                                  {line.label}
                                </span>
                                {' — '}
                                {line.error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {canToggleError && (
                        <button
                          type="button"
                          data-testid={`execution-error-toggle-${execution.id}`}
                          aria-expanded={isErrorExpanded}
                          onClick={(e) => {
                            // Não seleciona a execução: o clique para aqui.
                            e.stopPropagation();
                            toggleErrorExpanded(execution.id);
                          }}
                          className="mt-1 text-[11px] font-semibold hover:underline underline-offset-2"
                          style={{ color: 'var(--theme-error-color)' }}
                        >
                          {isErrorExpanded ? 'ver menos' : 'ver mais'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with stats (mockup .side-f > .side-counts) */}
      {!isLoading && !error && executions.length > 0 && (
        <div
          className="px-4 py-[11px] border-t flex-shrink-0 flex items-center gap-3"
          style={{
            backgroundColor: 'var(--theme-surface-sunken)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <span className="text-[11.5px]" style={{ color: 'var(--theme-muted-text)' }}>
            {executions.length} execução(ões)
          </span>
          <div className="flex items-center gap-3 ml-auto text-[11.5px]" style={{ color: 'var(--theme-muted-text)' }}>
            {/* Completed count */}
            {executions.filter((e) => e.status === 'completed').length > 0 && (
              <span>
                <b className="tabular-nums" style={{ color: 'var(--theme-text)' }}>
                  {executions.filter((e) => e.status === 'completed').length}
                </b>{' '}
                concluídas
              </span>
            )}
            {/* Failed count */}
            {executions.filter((e) => e.status === 'failed').length > 0 && (
              <span>
                <b className="tabular-nums" style={{ color: 'var(--theme-text)' }}>
                  {executions.filter((e) => e.status === 'failed').length}
                </b>{' '}
                falhas
              </span>
            )}
            {/* Running count */}
            {executions.filter((e) => e.status === 'running').length > 0 && (
              <span>
                <b className="tabular-nums" style={{ color: 'var(--theme-text)' }}>
                  {executions.filter((e) => e.status === 'running').length}
                </b>{' '}
                em execução
              </span>
            )}
            {/* Suspended count */}
            {executions.filter((e) => e.status === 'suspended').length > 0 && (
              <span>
                <b className="tabular-nums" style={{ color: 'var(--theme-text)' }}>
                  {executions.filter((e) => e.status === 'suspended').length}
                </b>{' '}
                aguardando
              </span>
            )}
            {/* Cancelled count */}
            {executions.filter((e) => e.status === 'cancelled').length > 0 && (
              <span data-testid="execution-history-cancelled-count">
                <b className="tabular-nums" style={{ color: EXECUTION_STATUS_COLORS.cancelled }}>
                  {executions.filter((e) => e.status === 'cancelled').length}
                </b>{' '}
                cancelados
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ExecutionHistorySidebar.displayName = 'ExecutionHistorySidebar';

export default ExecutionHistorySidebar;
